/**
 * Subscribe to /api/plans/:id/progress via EventSource and expose the last
 * N progress events, derived warning state, and mismatches to the caller.
 *
 * Only connects when `enabled` is true and the workspace is loaded. The
 * wid query param is mandatory — the backend auth middleware reads workspace
 * membership from it for cookies-only EventSource handshakes.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { useWorkspaceOrNull } from '@/_shared/contexts/WorkspaceContext';

import type { InstrumentationProgress } from '../types';

export interface UsePrGenerationProgressReturn {
  events: InstrumentationProgress[];
  connected: boolean;
  hasWarnings: boolean;
  mismatches: NonNullable<InstrumentationProgress['mismatches']>;
}

export function usePrGenerationProgress(planId: string, enabled: boolean): UsePrGenerationProgressReturn {
  const ws = useWorkspaceOrNull();
  const workspaceId = ws?.workspace.id ?? null;
  const [events, setEvents] = useState<InstrumentationProgress[]>([]);
  const [connected, setConnected] = useState(false);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    setEvents([]);
    if (!enabled || !planId || !workspaceId) return;

    // The auth middleware resolves workspace membership from the `wid` query
    // parameter for EventSource handshakes (cookies-only — EventSource cannot
    // send custom headers). Without ?wid=, auth middleware 401s.
    const url = `/api/plans/${planId}/progress?wid=${encodeURIComponent(workspaceId)}`;
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;

    const onOpen = (): void => setConnected(true);
    const onError = (): void => setConnected(false);
    const onProgress = (ev: MessageEvent): void => {
      try {
        const parsed = JSON.parse(ev.data as string) as InstrumentationProgress;
        setEvents((prev) => [...prev, parsed]);
      } catch {
        /* ignore malformed event */
      }
    };

    es.addEventListener('open', onOpen);
    es.addEventListener('error', onError);
    es.addEventListener('instrumentation_progress', onProgress);

    return () => {
      es.removeEventListener('open', onOpen);
      es.removeEventListener('error', onError);
      es.removeEventListener('instrumentation_progress', onProgress);
      es.close();
      setConnected(false);
    };
  }, [planId, enabled, workspaceId]);

  // Memoize so the caller gets a stable reference when events hasn't changed.
  // Without this, the array identity flips every render and any downstream
  // memo (e.g. banner rendering keyed off mismatches.length) would churn.
  const mismatches = useMemo(
    () => events.filter((e) => e.status === 'validation_warning' && e.mismatches).flatMap((e) => e.mismatches ?? []),
    [events],
  );

  return {
    events,
    connected,
    hasWarnings: mismatches.length > 0,
    mismatches,
  };
}
